import Text from 'components/Text/Text';
import VideoUploaderByEntityId from 'components/VideoUploader/VideoUploaderByEntityId';
import { RequestEntity } from 'types';
import { useAdditionalValidation } from 'components/WithAdditionalValidation/useAdditionalValidation';
import React, { useContext, useState } from 'react';
import { postMediaFile } from 'backend/additionalInformation';
import LoaderWithProps from 'components/Loader/LoaderWithProps';
import breadcrumbs from 'images/breadcrumbs.svg';
import { EditTeaserContext, IEditTeaserContext } from 'modules/Teasers/EditTeaser/editTeaserState/editTeaserContext';
import classNames from 'classnames';
import style from './FormDataTeaserUploader.module.scss';

const FormDataTeaserUploader: React.FC = () => {
	const { changeTypeOfModalWindow, setFile, deleteVideo, teaserID, fileId, filePath, sourceUrlLocally } =
		useContext<IEditTeaserContext>(EditTeaserContext);
	const [isShowLoader, setIsShowLoader] = useState<boolean>(false);
	const { isShowValidationError, validationErrorMessage } = useAdditionalValidation(
		fileId || filePath,
		'uploadVideoNecessary',
		'teaserVideo',
	);

	const imgBlankClasses = classNames(style.imgBlank, { [style.error]: isShowValidationError });
	const uploadBtnClasses = classNames(style.btn, style.addOrEditBtn, style.posAbsolute, {
		[style.visible]: !sourceUrlLocally && !filePath,
	});
	const replaceBtnClasses = classNames(style.btn, style.addOrEditBtn, {
		[style.visible]: sourceUrlLocally || filePath,
	});
	const deleteBtnClasses = classNames(style.btn, style.deleteBtn, {
		[style.visible]: sourceUrlLocally || filePath,
	});

	const sendForm = async (video: File) => {
		setIsShowLoader(true);
		const formData = new FormData();
		formData.append('file', video, video.name);
		const sourceUrl = URL.createObjectURL(video);
		try {
			const response = await postMediaFile({ data: formData });
			setFile(response.data.fileId, sourceUrl);
			setIsShowLoader(false);
		} catch (err) {
			setIsShowLoader(false);
			changeTypeOfModalWindow('fileUploadError');
		}
	};

	const handleChangeFile = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.preventDefault();
		const maxFileSizeMB = 50;
		const videoFile = e.target.files ? e.target.files[0] : '';
		if (!videoFile) {
			return;
		} else if (videoFile.size > 1000 * 1000 * maxFileSizeMB) {
			changeTypeOfModalWindow('videoFileSizeExceeded');
		} else if (videoFile.type === 'video/mp4' || videoFile.type === 'video/x-m4v') {
			sendForm(videoFile);
		} else {
			changeTypeOfModalWindow('invalidFileFormat');
		}
		e.target.value = '';
	};

	return (
		<div className={style.container} data-test="FormDataTeaserUploader">
			<div className={style.imgWrapper}>
				{filePath && !sourceUrlLocally ? (
					<VideoUploaderByEntityId
						resource={RequestEntity.Screensavers}
						entityID={teaserID}
						videoSize="Original"
						loaderSize="big"
						isShowControls={true}
					/>
				) : null}
				{sourceUrlLocally ? (
					<div className={style.fileImgWrapper}>
						<video className={style.img} src={sourceUrlLocally} controls={true} />
					</div>
				) : null}
				{!sourceUrlLocally && !filePath ? (
					<>
						<div className={imgBlankClasses}>
							<svg>
								<use xlinkHref={`${breadcrumbs}#teaserDefaultAvatar`} />
							</svg>
						</div>
						{isShowValidationError && <div className={style.imgErrorText}>{validationErrorMessage}</div>}
					</>
				) : null}
				{isShowLoader ? (
					<div className={style.loaderWrapper}>
						<LoaderWithProps loaderSize="small" />
					</div>
				) : null}
			</div>
			<input
				id="teaserVideo"
				className={style.hidden}
				type="file"
				accept="video/mp4,video/x-m4v"
				onChange={handleChangeFile}
			/>
			<div className={style.btnWrapper}>
				<label className={style.label} htmlFor="teaserVideo">
					<div className={uploadBtnClasses}>
						<svg>
							<use xlinkHref={`${breadcrumbs}#uploadPhoto`} />
						</svg>
						<Text translationNameSpace="form" translationKey="uploadTeaserButton" asFragment={true} />
					</div>
					<div className={replaceBtnClasses}>
						<svg>
							<use xlinkHref={`${breadcrumbs}#reload`} />
						</svg>
						<Text translationNameSpace="form" translationKey="replaceTeaserButton" asFragment={true} />
					</div>
				</label>
				<div className={deleteBtnClasses} onClick={deleteVideo}>
					<svg>
						<use xlinkHref={`${breadcrumbs}#bin`} />
					</svg>
					<Text translationNameSpace="form" translationKey="deleteTeaserButton" asFragment={true} />
				</div>
			</div>
		</div>
	);
};

export default FormDataTeaserUploader;
